import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FETCH_PERSONNEL_LIST_REQUEST,
  FETCH_PERSONNEL_LIST_SUCCESS,
  FETCH_PERSONNEL_LIST_FAILURE,
} from '../actions/schoolActions';
import { schoolYearDataType } from '../actions/schoolActions';
// Define the shape of personnel data
export interface PersonnelData {
  id: string;
  code: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  role: string;
  enrollment: any;
}

export interface SchoolDetailState {
  id: string;
  name: string;
}
export interface SchoolYearItem {
  id: number;
  name: string;
  year: number;
  start_date: string;
  end_date: string;
  status: number;
  check: boolean;
  created_date: string;
  updated_date: string;
}

// Type cho links
export interface PaginationLinks {
  next: string | null;
  previous: string | null;
}

// Type cho API response tổng thể
export interface SchoolYearResponse {
  links: PaginationLinks;
  count: number;
  page_size: number;
  results: SchoolYearItem[];
}
export interface SchoolYearCreationRes {
  id: number;
  name: string;
  year: number;
  start_date: string;
  end_date: string;
  status: number;
  created_date: string;
  updated_date: string;
  school: number;
}
export interface UpgradeClassesItem {
  grade: string;
  old_school_year: number;
  next_school_year: number;
  total_class_upgrade: number;
  classes: ClassUpgrade[];
}

export interface ClassUpgrade {
  old_class: ClassInfo;
  new_class: ClassInfo;
}

export interface ClassInfo {
  class_name: string;
  student_count: number;
  school_year: string;
}
export interface SchoolState {
  schoolList: {
    count?: number;
    results?: SchoolDetailState[];
  };
  schoolCreation: any;
  schoolModules: any[];
  loading: boolean;
  provinceList: any[];
  districtList: any[];
  wardList: any[];
  schoolYearList: SchoolYearResponse;
  successfulSchoolYear: SchoolYearCreationRes;
  UpdateSuccessfulSchoolYear: {};
  loadingProvince: boolean;
  loadingDistrict: boolean;
  loadingWard: boolean;
  schoolModuleList: any[];
  personnelList: PersonnelData[];
  sumPersonnelList: string;
  personnelLoading: boolean;
  personnelError: null | string;
  UpgradePreviewClass: UpgradeClassesItem[];
  UpgradeClassLogs: any;
  loadingUpgradeClasLogs: boolean;
  previewUpgradeLoading: boolean;
  loadingDownloadTemplateSchool: false;
  templateSchoolFile: null;
  loadingSchoolModuleGrade: boolean;
  loadingDeleteSchoolModuleGrade: boolean;
  loadingDeleteSchool: boolean;
  deleteSchoolSuccess: boolean;
  schoolModuleGradeSuccessTick: number;
}

const initialState: SchoolState = {
  schoolList: { count: 0, results: [] },
  schoolCreation: {},
  schoolModules: [],
  loading: false,
  provinceList: [],
  districtList: [],
  wardList: [],
  schoolYearList: { links: null, count: 0, page_size: 0, results: [] },
  loadingProvince: false,
  loadingDistrict: false,
  loadingWard: false,
  schoolModuleList: [],
  personnelList: [],
  sumPersonnelList: null,
  personnelLoading: false,
  personnelError: null,
  successfulSchoolYear: null,
  UpdateSuccessfulSchoolYear: null,
  UpgradePreviewClass: null,
  previewUpgradeLoading: false,
  UpgradeClassLogs: null,
  loadingUpgradeClasLogs: false,
  loadingDownloadTemplateSchool: false,
  templateSchoolFile: null,
  loadingSchoolModuleGrade: false,
  loadingDeleteSchoolModuleGrade: false,
  loadingDeleteSchool: false,
  deleteSchoolSuccess: false,
  schoolModuleGradeSuccessTick: 0,
};

const schoolSlice = createSlice({
  name: 'school',
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setSchoolList(state, action) {
      state.schoolList = action.payload;
    },
    setPreviewLoading(state, action) {
      state.previewUpgradeLoading = action.payload;
    },
    setSchoolCreation(state, action) {
      state.schoolCreation = action.payload;
    },
    setListSchoolModule(state, action) {
      state.schoolModules = action.payload;
    },
    clearSchoolData(state) {
      state.schoolList = {
        count: 0,
        results: [],
      };
      state.schoolCreation = {};
    },
    setProvinceList(state, action: PayloadAction<any>) {
      state.provinceList = action.payload?.results;
    },
    setDistrictList(state, action: PayloadAction<any>) {
      state.districtList = action.payload;
    },
    setWardList(state, action: PayloadAction<any>) {
      state.wardList = action.payload;
    },
    setLoadingProvince(state, action: PayloadAction<any>) {
      state.loadingProvince = action.payload;
    },
    setLoadingDistrict(state, action: PayloadAction<any>) {
      state.loadingDistrict = action.payload;
    },
    setLoadingWard(state, action: PayloadAction<any>) {
      state.loadingWard = action.payload;
    },
    setSchoolModuleList(state, action: PayloadAction<any>) {
      state.schoolModuleList = action.payload;
    },
    setPersonnelList(state, action: PayloadAction<PersonnelData[]>) {
      state.personnelList = action.payload;
    },
    setSumPersonnelList(state, action: any) {
      state.sumPersonnelList = action.payload;
    },
    setPersonnelLoading(state, action: PayloadAction<boolean>) {
      state.personnelLoading = action.payload;
    },
    setPersonnelError(state, action: PayloadAction<null | string>) {
      state.personnelError = action.payload;
    },
    setSchoolYearsList(state, action: PayloadAction<any>) {
      state.schoolYearList = action.payload;
    },
    setSuccessfulSchoolYear(state, action: PayloadAction<any>) {
      state.successfulSchoolYear = action.payload;
    },
    setUpdateSuccessfulSchoolYear(state, action: PayloadAction<any>) {
      state.UpdateSuccessfulSchoolYear = action.payload;
    },
    setPreviewUpgradeClass(state, action: PayloadAction<any>) {
      state.UpgradePreviewClass = action.payload;
    },
    setUpgradeClassLogs(state, action: PayloadAction<any>) {
      state.UpgradeClassLogs = action.payload;
    },
    setLoadingUpgradeClassLogs(state, action) {
      state.loadingUpgradeClasLogs = action.payload;
    },
    clearPreviewUpgrade(state) {
      state.UpgradePreviewClass = null;
    },
    clearUpgradeClassLogs(state) {
      state.UpgradeClassLogs = null;
    },
    setLoadingDownloadTemplateSchool(state, action: PayloadAction<any>) {
      state.loadingDownloadTemplateSchool = action.payload
    },
    setTemplateSchoolFile(state, action: PayloadAction<any>) {
        state.templateSchoolFile = action.payload
    },
    clearTemplateSchoolFile(state) {
        console.log('clearTemplateSchoolFile')
        state.templateSchoolFile = null
    },
    setLoadingSchoolModuleGrade(state, action: PayloadAction<boolean>) {
      state.loadingSchoolModuleGrade = action.payload;
    },
    setLoadingDeleteSchoolModuleGrade(state, action: PayloadAction<boolean>) {
      state.loadingDeleteSchoolModuleGrade = action.payload;
    },
    setLoadingDeleteSchool(state, action: PayloadAction<boolean>) {
      state.loadingDeleteSchool = action.payload;
    },
    setDeleteSchoolSuccess(state, action: PayloadAction<boolean>) {
      state.deleteSchoolSuccess = action.payload;
    },
    incrementSchoolModuleGradeSuccess(state) {
      state.schoolModuleGradeSuccessTick += 1;
    },
  },
});

export const {
  setLoading,
  setSchoolList,
  setSchoolCreation,
  clearSchoolData,
  setListSchoolModule,
  setProvinceList,
  setDistrictList,
  setWardList,
  setLoadingProvince,
  setLoadingDistrict,
  setLoadingWard,
  setSchoolModuleList,
  setPersonnelList,
  setSumPersonnelList,
  setPersonnelLoading,
  setPersonnelError,
  setSchoolYearsList,
  setSuccessfulSchoolYear,
  setPreviewUpgradeClass,
  setPreviewLoading,
  setLoadingUpgradeClassLogs,
  setUpgradeClassLogs,
  clearPreviewUpgrade,
  clearUpgradeClassLogs,
  setLoadingDownloadTemplateSchool,
  setTemplateSchoolFile,
  clearTemplateSchoolFile,
  setLoadingSchoolModuleGrade,
  setLoadingDeleteSchoolModuleGrade,
  setLoadingDeleteSchool,
  setDeleteSchoolSuccess,
  incrementSchoolModuleGradeSuccess,
} = schoolSlice.actions;

export default schoolSlice.reducer;
